import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Grid, Typography } from "@material-ui/core";
import { AgGridReact } from "ag-grid-react";
import "./QuotaAttainmentDrilldown.scss";
import Skeleton from "@material-ui/lab/Skeleton";
import * as d3 from "d3";
import {
  _isNaN,
  getCSVFileNameString,
  dateFormatterv2,
  dateFormatterv3,
} from "../../util/customFunctions";
import HoverableIcon from "../../components/Buttons/HoverableIcon ";
import { store } from "../../util/store";

const useStyle = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  tableHeader: {
    whiteSpace: "normal",
    textAlign: "center",
  },
  tableCell: {
    whiteSpace: "normal",
  },
  titleContainer: {
    width: "100%",
  },
}));

const QuotaAttainmentDrilldown = (props) => {
  const Symbol = store.getState();
  const Currency_Symbol = Symbol.app.user.Currency;

  const classes = useStyle();
  const containerHeight = props.data
    ? props.data.opportunities.length * 35 < window.innerHeight - 300
      ? "-"
      : window.innerHeight - 300
    : 500;
  const domLayout = props.data
    ? props.data.opportunities.length * 35 < window.innerHeight - 300
      ? "autoHeight"
      : "normal"
    : "normal";
  const attainment = props.data.label;
  const uniqueReps = props.data.uniqueRep;

  const defaultColDef = {
    flex: 1,
    cellClass: "cell-wrap-text",
    wrapText: true,
    autoHeight: true,
    sortable: true,
    resizable: true,
    colResizeDefault: "shift",
  };

  let gridApi, columnApi, params;

  const gridReady = (params) => {
    gridApi = params.api;
    columnApi = params.columnApi;
    params = params;
  };
  const onFirstDataRendered = (params) => {
    if (window.innerWidth < 500) {
      var allColumns = params.columnApi.getAllColumns();
      var allColumnIds = allColumns.map((c) => c.colId);
      columnApi.autoSizeColumns(allColumnIds, true);
    }
  };

  const onColumnResized = (params) => {
    params.api.resetRowHeights();
  };

  const deltaIndicator = (params) => {
    const element = document.createElement("span");
    const imageElement = document.createElement("span");
    if (uniqueReps.includes(params.value) && props.data.cell === "total") {
      imageElement.appendChild(document.createTextNode("*"));
      imageElement.style.color = "#ed7d31";
    } else {
      element.appendChild(document.createTextNode(""));
    }
    element.appendChild(document.createTextNode(params.value));
    element.appendChild(imageElement);
    return element;
  };

  const exportCSV = (params) => {
    var params = {
      skipHeader: false,
      skipFooters: true,
      allColumns: true,
      onlySelected: false,
      suppressQuotes: true,
      processCellCallback: (cellParams) => {
        const rowIndex = cellParams.column.colId;
        const dateTypes = props.body.columns
          .filter((f) => f.type === "date")
          .map((f) => f.field);
        const dateTimeTypes = props.body.columns
          .filter((f) => f.type === "datetime")
          .map((f) => f.field);
        const stringTypes = props.body.columns
          .filter((f) => f.type === "string")
          .map((f) => f.field);
        const integerTypes = props.body.columns
          .filter((f) => f.type === "integer")
          .map((f) => f.field);
        const currencyTypes = props.body.columns
          .filter((f) => f.type === "currency")
          .map((f) => f.field);
        if (cellParams && dateTypes.includes(rowIndex)) {
          return cellParams.value ? dateFormatterv2(cellParams.value) : null; //apply your timestamp formatter
        } else if (cellParams && stringTypes.includes(rowIndex)) {
          return `"${cellParams.value}"`; //apply your string formatter
        } else if (cellParams && dateTimeTypes.includes(rowIndex)) {
          return cellParams.value ? dateFormatterv3(cellParams.value) : null; //apply your timestamp formatter
        } else return cellParams.value; // no formatting
      },
      fileName: getCSVFileNameString(props.header),
      columnSeparator: ",",
    };
    gridApi.exportDataAsCsv(params);
  };
  return (
    <Grid container className={classes.root}>
      {props.data ? (
        <React.Fragment>
          <Grid
            container
            justify="space-betweeen"
            className={classes.titleContainer}
            xs={12}
            style={{ padding: "0 8rem" }}
          >
            <Grid
              item
              container
              xs={10}
              justify="flex-start"
              alignItems="flex-end"
            >
              <Typography variant="body1" style={{ fontWeight: "600" }}>
                Attainment Range: <span data-test="count">{attainment}</span>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Number of Reps:{" "}
                <span data-test="acv">{props.data.repNo}</span>
              </Typography>
            </Grid>

            <Grid
              direction="column"
              item
              container
              xs={2}
              justify="flex-end"
              alignItems="flex-end"
              alignContent="flex-end"
            >
              <Grid item justify="flex-end" style={{ paddingBottom: "1rem" }}>
                <button style={{ border: "none" }} onClick={exportCSV}>
                  <HoverableIcon />
                </button>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            container
            xs={12}
            justify="center"
            alignContent="center"
            alignItems="center"
          >
            <Grid
              item
              className="ag-theme-custom-react_DB"
              style={{
                height: containerHeight,
                width: "100%",
                padding: "0 5rem",
              }}
            >
              <AgGridReact
                components={{
                  deltaIndicator: deltaIndicator,
                }}
                columnDefs={[
                  {
                    headerName: "Number",
                    field: "number",
                    type: "rightAligned",
                    sortable: false,
                    maxWidth: 70,
                  },
                  {
                    headerName: "Attainment Range",
                    field: "attainmentRange",
                    flex: 1.3,
                    suppressSizeToFit: true,
                    sortable: true,
                    filter: true,
                  },
                  {
                    headerName: "Rep",
                    field: "Owner_Name",
                    flex: 3,
                    sortable: true,
                    filter: true,
                    cellRenderer: "deltaIndicator",
                  },
                  {
                    headerName: "Quota",
                    field: "Quota",
                    flex: 1,
                    // type: "rightAligned",
                    cellClass: "ag-right-aligned-cell",
                    sortable: true,
                    filter: "agNumberColumnFilter",
                    filterParams: { defaultOption: "greaterThanOrEqual" },
                    filterParams: { defaultOption: "greaterThan" },
                    sortingOrder: ["desc", "asc"],
                    valueFormatter: (x) => {
                      if (_isNaN(Math.round(x.value))) {
                        return "";
                      }
                      return (
                        `${Currency_Symbol} ` +
                        d3.format(",")(Math.round(x.value))
                      );
                    },
                  },
                  {
                    headerName: "Actual",
                    field: "acv",
                    flex: 1,
                    // type: "rightAligned",
                    cellClass: "ag-right-aligned-cell",
                    sortable: true,
                    filter: "agNumberColumnFilter",
                    filterParams: { defaultOption: "greaterThanOrEqual" },
                    filterParams: { defaultOption: "greaterThan" },
                    sortingOrder: ["desc", "asc"],
                    valueFormatter: (x) => {
                      if (_isNaN(Math.round(x.value))) {
                        return "";
                      }
                      return (
                        `${Currency_Symbol} ` +
                        d3.format(",")(Math.round(x.value))
                      );
                    },
                  },
                  {
                    headerName: "Attainment % ",
                    field: "attainment",
                    flex: 1,
                    // type: "rightAligned",
                    cellClass: "ag-right-aligned-cell",
                    sortable: true,
                    filter: "agNumberColumnFilter",
                    filterParams: { defaultOption: "greaterThanOrEqual" },
                    filterParams: { defaultOption: "greaterThan" },
                    sortingOrder: ["desc", "asc"],
                    valueFormatter: (x) => {
                      if (_isNaN(Math.round(x.value))) {
                        return "";
                      }
                      return Math.round(x.value * 100) + "%";
                    },
                    resizable: false,
                  },
                ]}
                sizeColumnsToFit={true}
                headerHeight="55"
                rowData={props.data.opportunities.map((item, index) => {
                  let temp = {
                    ...item,
                    attainmentRange:
                      attainment === "All"
                        ? Math.round(item.attainment * 100) === 0
                          ? "0%"
                          : Math.round(item.attainment * 100) > 0 &&
                            Math.round(item.attainment * 100) <= 25
                          ? "0-25%"
                          : Math.round(item.attainment * 100) > 25 &&
                            Math.round(item.attainment * 100) <= 50
                          ? "25-50%"
                          : Math.round(item.attainment * 100) > 50 &&
                            Math.round(item.attainment * 100) <= 75
                          ? "50-75%"
                          : Math.round(item.attainment * 100) > 75 &&
                            Math.round(item.attainment * 100) <= 100
                          ? "75-100%"
                          : Math.round(item.attainment * 100) > 100 &&
                            Math.round(item.attainment * 100) <= 125
                          ? "100-125%"
                          : Math.round(item.attainment * 100) > 125 &&
                            Math.round(item.attainment * 100) <= 150
                          ? "125-150%"
                          : Math.round(item.attainment * 100) > 150 &&
                            Math.round(item.attainment * 100) <= 175
                          ? "150-175%"
                          : Math.round(item.attainment * 100) > 175 &&
                            Math.round(item.attainment * 100) <= 200
                          ? "175-200%"
                          : Math.round(item.attainment * 100) > 200
                          ? ">200%"
                          : attainment
                        : attainment,
                    number: attainment === "All" ? index + 1 : item.number,
                  };
                  return temp;
                })}
                onFirstDataRendered={onFirstDataRendered}
                defaultColDef={defaultColDef}
                domLayout={domLayout}
                onColumnResized={onColumnResized}
                enableCellTextSelection={true}
                colResizeDefault={"shift"}
                debounceVerticalScrollbar={true}
                onGridReady={gridReady}
              ></AgGridReact>
            </Grid>
          </Grid>
          {props.data.cell === "total" &&
            uniqueReps.length > 0 &&
            props.data.opportunities.filter((x) => x.Owner_Name).length > 0 &&
            uniqueReps
              .map(
                (owner) =>
                  props.data.opportunities.filter((x) => x.Owner_Name === owner)
                    .length
              )
              .reduce((a, b) => a + b, 0) <= uniqueReps.length &&
            uniqueReps
              .map(
                (owner) =>
                  props.data.opportunities.filter((x) => x.Owner_Name === owner)
                    .length
              )
              .reduce((a, b) => a + b, 0) > 0 && (
              <Grid
                item
                container
                xs={10}
                justify="flex-start"
                alignItems="flex-end"
                style={{ marginTop: "1rem" }}
              >
                <Typography variant="body2" style={{ paddingLeft: "8rem" }}>
                  <span style={{ color: "#ED7D31" }}>*</span>&nbsp; Excluded
                  from analysis for time periods where no quota data is
                  available.
                </Typography>
              </Grid>
            )}
        </React.Fragment>
      ) : (
        <Skeleton
          animation="wave"
          variant="rect"
          width="100%"
          height={280}
          style={{ margin: "20px 0px" }}
        />
      )}
    </Grid>
  );
};

export default QuotaAttainmentDrilldown;
